import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { faFilter, faGear, faPlay } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tabs from '@frontend/Components/Tabs';
import FilterDropdown from '@frontend/Components/FilterDropdown';
import SearchBar from '@frontend/Components/Inputs/SearchBar/SearchBar';
import PageHeader from '@frontend/Components/Titles/PageHeader/PageHeader';
import CreateButton from '@frontend/Components/CreateButton/CreateButton';
import BidsTable from './Components/BidsTable';
import BidSchedule from './Components/BidSchedule';
import ToastService from '@frontend/AppSettings/services/ToastService';
import { useBids } from './useBids';
import { hydrateFailed, refresh } from './bidStore';
import { createDraftBid, type Bid } from './bidStore';
import './BidSheetsPage.css';
import './bidDetailShared.css';
// Subscribing re-renders this tree when the capability resolves — financialDisplay's
// flag is module state, so without a subscriber money would stay redacted forever.
import { useBidCapabilities } from './useBidCapabilities';
import { useFinancialVisible } from './financialDisplay';

type BidTab = 'all' | 'open' | 'schedule';

const BidSheetsPage = (): JSX.Element => {
  // The value is read now (the reference-data cog below is capability-gated), not just subscribed
  // for its side effect. null until resolved and denied on failure, so the cog appears only once
  // the server has confirmed the caller may administer catalogs.
  const capabilities = useBidCapabilities();
  useFinancialVisible();
  const navigate = useNavigate();
  const bids = useBids();

  const [loading, setLoading] = useState(false);

  // Create a draft on the backend, then open it. Surfaces a toast if the POST fails so the
  // button never appears inert (and the promise rejection is handled, not swallowed).
  const createAndOpen = async (query = ''): Promise<void> => {
    setLoading(true);
    try {
      const id = await createDraftBid();
      navigate(`/bids/${id}${query}`);
    } catch (err) {
      ToastService.error('Could not create a new bid. Please try again.');
      console.error('createDraftBid failed:', err);
      setLoading(false);
    }
  };
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromUrl = (searchParams.get('tab') as BidTab) || 'all';
  const [activeTab, setActiveTab] = useState<BidTab>(tabFromUrl);

  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [estimatorFilter, setEstimatorFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(7);

  // Keep the active tab in sync with the URL (so the nav "Bid Schedule" link works).
  useEffect(() => {
    const urlTab = (searchParams.get('tab') as BidTab) || 'all';
    setActiveTab(urlTab);
  }, [searchParams]);

  const filterOptions = useMemo(() => {
    const statuses = new Set<string>();
    const clients = new Set<string>();
    const estimators = new Set<string>();
    bids.forEach((b) => {
      if (b.status) statuses.add(b.status);
      if (b.client) clients.add(b.client);
      if (b.estimator) estimators.add(b.estimator);
    });
    return {
      statuses: Array.from(statuses).sort().map((v) => ({ value: v, label: v })),
      clients: Array.from(clients).sort().map((v) => ({ value: v, label: v })),
      estimators: Array.from(estimators).sort().map((v) => ({ value: v, label: v })),
    };
  }, [bids]);

  const filteredBids = useMemo<Bid[]>(() => {
    const term = searchTerm.trim().toLowerCase();
    return bids.filter((b) => {
      if (activeTab === 'open' && (b.status === 'Won' || b.status === 'Lost')) return false;
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;
      if (clientFilter !== 'all' && b.client !== clientFilter) return false;
      if (estimatorFilter !== 'all' && b.estimator !== estimatorFilter) return false;
      if (term) {
        const haystack = `${b.id} ${b.name} ${b.client} ${b.area} ${b.estimator}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [bids, activeTab, statusFilter, clientFilter, estimatorFilter, searchTerm]);

  const pageLimit = Math.max(1, Math.ceil(filteredBids.length / pageSize));
  const pagedBids = useMemo(
    () => filteredBids.slice((page - 1) * pageSize, page * pageSize),
    [filteredBids, page, pageSize],
  );

  const handleTabChange = (tabId: string): void => {
    const next = tabId as BidTab;
    setActiveTab(next);
    setPage(1);
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="mattersPage">
      <PageHeader title="Bids" subtitle="Track, build, and schedule project bids">
        <div className="bids-header-actions">
          <button
            className="bids-walk-btn"
            onClick={() => { void createAndOpen('?walkthrough=1'); }}
            title="Watch a full bid get built end to end"
          >
            <FontAwesomeIcon icon={faPlay} /> Play walk-through
          </button>
          <CreateButton
            onClick={() => { void createAndOpen(); }}
            text="New Bid"
            showIcon
            isLoading={loading}
            aria-label="Create new bid"
          />
        </div>
      </PageHeader>

      <div className="matters-content-container">
        <div className="matters-tabs-search-section">
          <div className="matters-tabs-section">
            <Tabs
              tabs={[
                { id: 'all', label: 'All Bids' },
                { id: 'open', label: 'Open Bids' },
                { id: 'schedule', label: 'Bid Schedule' },
              ]}
              activeTabId={activeTab}
              onTabChange={handleTabChange}
              variant="pills"
            />
            {activeTab !== 'schedule' && (
              <span className="matters-count">
                Showing {pagedBids.length} of {filteredBids.length} bids
              </span>
            )}
          </div>
          {activeTab !== 'schedule' && (
            <div className="matters-search-section">
              <SearchBar
                onSearch={(term) => { setSearchTerm(term || ''); setPage(1); }}
                placeholder="Search bids..."
                className="matters-search-bar"
              />
              <button className="matters-filter-icon-button" aria-label="Filter" title="Filter">
                <FontAwesomeIcon icon={faFilter} />
              </button>
              {/*
                Reference-data admin lives here rather than in the sidebar: it is a setting FOR this
                screen (the catalogs these bids price from), not a destination of its own.

                Gated on the CAPABILITY, never on permish — the same check the API enforces on every
                catalog write. `useBidCapabilities` is null until resolved and denies on failure, so
                the cog never appears for someone the server would refuse. Hiding it is cosmetic
                anyway: /bids/admin is wrapped in RequireBidAdmin and every endpoint re-checks.
              */}
              {capabilities?.isBidAdmin && (
                <button
                  className="matters-filter-icon-button"
                  aria-label="Bid reference data"
                  title="Bid reference data"
                  onClick={() => navigate('/bids/admin')}
                >
                  <FontAwesomeIcon icon={faGear} />
                </button>
              )}
            </div>
          )}
        </div>

        {activeTab !== 'schedule' && (
          <div className="matters-filters-row">
            <FilterDropdown
              label=""
              placeholder="All Statuses"
              options={[{ value: 'all', label: 'All Statuses' }, ...filterOptions.statuses]}
              value={statusFilter}
              onChange={(v) => { setStatusFilter(Array.isArray(v) ? v[0] : v); setPage(1); }}
              className="matters-filter-dropdown"
            />
            <FilterDropdown
              label=""
              placeholder="All Clients"
              options={[{ value: 'all', label: 'All Clients' }, ...filterOptions.clients]}
              value={clientFilter}
              onChange={(v) => { setClientFilter(Array.isArray(v) ? v[0] : v); setPage(1); }}
              className="matters-filter-dropdown"
            />
            <FilterDropdown
              label=""
              placeholder="All Estimators"
              options={[{ value: 'all', label: 'All Estimators' }, ...filterOptions.estimators]}
              value={estimatorFilter}
              onChange={(v) => { setEstimatorFilter(Array.isArray(v) ? v[0] : v); setPage(1); }}
              className="matters-filter-dropdown"
            />
          </div>
        )}
      </div>

      {hydrateFailed() && bids.length === 0 ? (
        // A failed list load is NOT an empty tenant — rendering "No bids found" for a network
        // failure told the user their bids were gone (gap review 0.11). The error REPLACES the
        // table: the first version rendered both, and the empty table's own "No bids found" sat
        // right under the banner contradicting it. (A failed REFRESH over an already-populated
        // cache deliberately keeps showing the stale list — data beats a banner there.)
        <div className="bid-detail-notfound">
          <p>Couldn't load bids.</p>
          <button className="bid-detail-back-btn" onClick={() => void refresh()}>Retry</button>
        </div>
      ) : activeTab === 'schedule' ? (
        <div className="schedule-view-wrapper">
          <BidSchedule bids={filteredBids} onBidClick={(bid) => navigate(`/bids/${bid.id}`)} />
        </div>
      ) : (
        <BidsTable
          data={pagedBids}
          pageSize={pageSize}
          pagination={{
            currPage: page,
            onPageChange: setPage,
            pageLimit,
            pageSize,
            onPageSizeChange: (n: number) => { setPageSize(n); setPage(1); },
            totalItems: filteredBids.length,
            itemLabel: 'bids',
          }}
        />
      )}
    </div>
  );
};

export default BidSheetsPage;
